









































































































































































import {
  defineComponent,
  reactive,
  ref,
  toRefs,
  PropType,
  computed,
  watch,
  SetupContext
} from "@vue/composition-api";
import OccTable from "@/components/Atoms/OccTable/index.vue";
import OccButton from "@/components/Atoms/OccButton/index.vue";
import OccCheckbox from "@/components/Atoms/OccCheckbox/index.vue";
import OccHeading from "@/components/Atoms/OccHeading/index.vue";
import OccLabel from "@/components/Atoms/OccLabel/index.vue";
import OccStar from "@/components/Atoms/OccStar/index.vue";

import { Order } from "@/api/types";

interface Header<
  T extends { sortParam: string; subHeaderSortParam?: string } = {
    sortParam: string;
    subHeaderSortParam: string;
  }
> {
  name: string;
  sortParam?: T["sortParam"];
  subHeaders?: {
    name: string;
    sortParam?: T["subHeaderSortParam"];
    order?: Order;
  }[];
  subHeaderSortParam?: { [key: string]: string };
  sizeContainer?: string;
  minSizeContainer?: string;
  sizeInner?: string;
  order?: Order;
  subHeaderSortOrder?: { [key: string]: Order };
}

export interface Props<
  T extends { sortParam: string; subHeaderSortParam?: string } = {
    sortParam: string;
    subHeaderSortParam: string;
  }
> {
  headers: Header<T>[];
  hideHeader: boolean;
  rows: object[];
  currentRowValues: any[];
  itemKey: string;
  checkbox: boolean;
  like: boolean;
  clickableRow: boolean;
  scroll: boolean;
  tableSize: string;
  hideBottomBorder: boolean;
  width: string;
}

export default defineComponent({
  components: {
    OccTable,
    OccButton,
    OccCheckbox,
    OccLabel,
    OccStar,
    OccHeading
  },
  props: {
    headers: {
      type: Array as PropType<Props["rows"]>,
      default: () => []
    },
    hideHeader: {
      type: Boolean as PropType<Props["hideHeader"]>,
      default: false
    },
    rows: {
      type: Array,
      default: () => [] as PropType<Props["rows"]>
    },
    currentRowValues: {
      type: Array as PropType<Props["currentRowValues"]>,
      default: () => []
    },
    itemKey: {
      type: String as PropType<Props["itemKey"]>,
      default: ""
    },
    checkbox: {
      type: Boolean as PropType<Props["checkbox"]>,
      default: false
    },
    like: {
      type: Boolean as PropType<Props["like"]>,
      default: false
    },
    clickableRow: {
      type: Boolean as PropType<Props["clickableRow"]>,
      default: false
    },
    scroll: {
      type: Boolean as PropType<Props["scroll"]>,
      default: false
    },
    tableSize: {
      type: String as PropType<Props["tableSize"]>,
      default: "medium",
      validator(value: string) {
        return ["small", "medium"].includes(value);
      }
    },
    hideBottomBorder: {
      type: Boolean as PropType<Props["hideBottomBorder"]>,
      default: false
    },
    width: {
      type: String as PropType<Props["width"]>,
      default: ""
    },
    gaEventCategory: {
      type: String,
      default: ""
    }
  },
  emits: [
    "changeRowValue",
    "changeOrder",
    "clickRow",
    "mouseover",
    "mouseleave"
  ],
  setup(props: Props, { emit }: SetupContext) {
    const state = reactive({
      dataCurrentRowValues: props.currentRowValues,
      dataOrder: "desc"
    });

    const checkedAll = ref<boolean>(false);
    const isIndeterminate = computed(() => {
      let indeterminate = false;
      if (!props.currentRowValues.length) {
        return false;
      }
      if (props.currentRowValues.length !== props.rows.length) {
        indeterminate = true;
      } else if (checkedAll.value) {
        indeterminate = false;
      }
      return indeterminate;
    });

    watch(
      () => props.currentRowValues,
      newValue => {
        state.dataCurrentRowValues = newValue;
        if (
          !props.currentRowValues.length ||
          props.currentRowValues.length !== props.rows.length
        ) {
          checkedAll.value = false;
        }
      }
    );

    const onChangeAll = () => {
      state.dataCurrentRowValues = [];
      if (checkedAll.value) {
        checkedAll.value = false;
        emit("changeRowValue", state.dataCurrentRowValues);
      } else {
        props.rows.forEach((_element, index) => {
          state.dataCurrentRowValues.push(
            props.itemKey
              ? (props.rows[index] as any)[props.itemKey]
              : props.rows[index]
          );
          emit("changeRowValue", state.dataCurrentRowValues);
        });
        checkedAll.value = true;
      }
    };

    const onChange = (value: any) => {
      if (state.dataCurrentRowValues.includes(value)) {
        const filteredArray = state.dataCurrentRowValues.filter(
          (currentValue: any) => currentValue !== value
        );
        state.dataCurrentRowValues = filteredArray;
        emit("changeRowValue", filteredArray);
      } else {
        state.dataCurrentRowValues.push(value);
        emit("changeRowValue", state.dataCurrentRowValues);
      }

      // 配列数を比較して同じじゃなければfalse
      if (state.dataCurrentRowValues.length !== props.rows.length) {
        checkedAll.value = false;
      } else {
        checkedAll.value = true;
      }
    };

    const valueChecked = (target: never) => {
      const checked: boolean = state.dataCurrentRowValues.includes(target);
      if (checked) {
        return true;
      } else {
        return false;
      }
    };

    const sortRequest = (index: number) => {
      state.dataOrder = props.headers[index].order === "desc" ? "asc" : "desc";
      emit("changeOrder", state.dataOrder, index);
    };

    const sortRequestSubColumn = (
      headerIndex: number,
      subHeaderIndex: number
    ) => {
      const header = props.headers[headerIndex] as Required<Header>;
      const subHeader = header.subHeaders[subHeaderIndex];
      if (!subHeader) return;

      state.dataOrder = subHeader.order === "desc" ? "asc" : "desc";
      emit(
        "changeOrderSubColumn",
        state.dataOrder,
        headerIndex,
        subHeaderIndex
      );
    };

    const rowClass = computed(() => {
      return function(row: any) {
        if (!props.clickableRow && !row.alert) return "";

        if (props.clickableRow && row.alert) {
          return "list-table__body-wrapper--clickableAlert";
        } else if (props.clickableRow && row.is_pre_entry_event) {
          return "list-table__body-wrapper--unClickable";
        } else if (props.clickableRow) {
          return "list-table__body-wrapper--clickable";
        } else {
          return "list-table__body-wrapper--alert";
        }
      };
    });

    const handleMouseOverTableHeader = () => {
      emit("mouseover");
    };

    const handleMouseLeaveTableHeader = () => {
      emit("mouseleave");
    };

    return {
      checkedAll,
      isIndeterminate,
      onChange,
      onChangeAll,
      valueChecked,
      ...toRefs(state),
      sortRequest,
      sortRequestSubColumn,
      rowClass,
      handleMouseOverTableHeader,
      handleMouseLeaveTableHeader
    };
  }
});
